import { useCallback, useEffect, useRef } from 'react';
import { INCOME_DOCUMENT_VERIFICATION_TIMEOUT_MS } from '@brighte/validation';
import {
  GetIncomeDocumentVerificationDocument,
  GetIncomeDocumentVerificationQuery,
  GetIncomeDocumentVerificationQueryVariables,
  IncomeDocumentVerificationStatus,
  VerifyIncomeDocumentUploadMutation,
  VerifyIncomeDocumentUploadMutationVariables,
  useFetcher,
  useRequestIncomeDocumentVerificationMutation,
} from '@graphql/index';

const POLL_INTERVAL_MS = 2_000;

export function useIncomeDocumentVerification() {
  const abortController = useRef(new AbortController());
  useEffect(() => {
    if (abortController.current.signal.aborted) abortController.current = new AbortController();
    const controller = abortController.current;
    return () => controller.abort();
  }, []);

  const { mutateAsync: requestVerification } = useRequestIncomeDocumentVerificationMutation({
    useErrorBoundary: false,
  });
  const getVerification = useFetcher<GetIncomeDocumentVerificationQuery, GetIncomeDocumentVerificationQueryVariables>(
    GetIncomeDocumentVerificationDocument,
  );
  // useFetcher returns a new function each render; keep the caller's verification effect stable.
  const getVerificationRef = useRef(getVerification);
  getVerificationRef.current = getVerification;

  const waitForCompletion = useCallback(async (input: GetIncomeDocumentVerificationQueryVariables['input']) => {
    const signal = abortController.current.signal;
    const checkCancelled = () => {
      if (signal.aborted) throw new DOMException('Income document verification cancelled.', 'AbortError');
    };
    const startedAt = Date.now();
    while (Date.now() - startedAt < INCOME_DOCUMENT_VERIFICATION_TIMEOUT_MS) {
      checkCancelled();
      const response = await getVerificationRef.current({ input });
      checkCancelled();
      const verification = response.getIncomeDocumentVerification;
      if (verification.verificationStatus !== IncomeDocumentVerificationStatus.PENDING) return verification;
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
    }
    throw new Error('Income document verification timed out.');
  }, []);

  const mutateAsync = useCallback(
    async ({
      VerifyIncomeDocumentUploadInput: input,
    }: VerifyIncomeDocumentUploadMutationVariables): Promise<VerifyIncomeDocumentUploadMutation> => {
      if (abortController.current.signal.aborted) {
        throw new DOMException('Income document verification cancelled.', 'AbortError');
      }
      await requestVerification({ input });
      const { result } = await waitForCompletion({
        applicationId: input.applicationId,
        documentIds: input.documentIds,
      });
      if (result) return { verifyIncomeDocumentUpload: result };
      throw new Error('Income document verification failed.');
    },
    [requestVerification, waitForCompletion],
  );

  return { mutateAsync, waitForCompletion };
}
